import React from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Switch,
  Tooltip,
  Rate,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

// Vive fuera del componente, asi que recibe `t` en vez de llamar al hook.
function previewFor(subtype, t) {
  if (subtype === "yes_no") {
    return (
      <Space size={6} wrap>
        <Tag>{t("formato.si")}</Tag>
        <Tag>{t("formato.no")}</Tag>
      </Space>
    );
  }
  if (subtype === "rating_1_3") return <Rate disabled count={3} value={0} />;
  if (subtype === "rating_1_5") return <Rate disabled count={5} value={0} />;
  if (subtype === "input_score") return <Tag>{t("formato.puntuacion")}</Tag>;
  return <Tag>—</Tag>;
}

export function EvaluationWizardStep3Questions({
  form,
  subtype,
  onMoveQuestion,
}) {
  // "Eliminar" ya existe en common: no se duplica en este namespace.
  const { t } = useTranslation(["evaluations", "common"]);

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Title level={5} style={{ marginTop: 0 }}>
            {t("asistente.paso3.titulo")}
          </Title>
          <Text type="secondary">{t("asistente.paso3.ayuda")}</Text>

          <div style={{ marginTop: 12 }}>
            <Form.List name="questions">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, idx) => (
                    <Card
                      key={field.key}
                      className="revi-eval-wizard__questionCard"
                      variant="outlined"
                      size="small"
                      title={
                        <Space size={8}>
                          {/*
                            SIN TRADUCIR A PROPOSITO: frase construida con el
                            numero de orden dentro. Hay que rehacerla como
                            plantilla con hueco, no sustituir una cadena.
                          */}
                          <Text strong>
                            {t("asistente.paso3.preguntaNumero", {
                              numero: idx + 1,
                            })}
                          </Text>
                          <Tag>{subtype}</Tag>
                        </Space>
                      }
                      extra={
                        <Space size={6}>
                          <Tooltip title={t("asistente.paso3.subir")}>
                            <Button
                              size="small"
                              icon={<ArrowUpOutlined />}
                              onClick={() => onMoveQuestion(idx, idx - 1)}
                              disabled={idx === 0}
                            />
                          </Tooltip>
                          <Tooltip title={t("asistente.paso3.bajar")}>
                            <Button
                              size="small"
                              icon={<ArrowDownOutlined />}
                              onClick={() => onMoveQuestion(idx, idx + 1)}
                              disabled={idx === fields.length - 1}
                            />
                          </Tooltip>
                          <Tooltip title={t("common:actions.delete")}>
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                              disabled={fields.length === 1}
                            />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Row gutter={[12, 12]}>
                        <Col xs={24} lg={18}>
                          <Form.Item
                            {...field}
                            name={[field.name, "text"]}
                            rules={[
                              {
                                required: true,
                                message: t(
                                  "asistente.validacion.textoPregunta",
                                ),
                              },
                              {
                                max: 180,
                                message: t("asistente.validacion.max180"),
                              },
                            ]}
                          >
                            <Input.TextArea
                              rows={2}
                              placeholder={t(
                                "asistente.paso3.placeholderPregunta",
                              )}
                              showCount
                              maxLength={180}
                            />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            name={[field.name, "id"]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                        </Col>

                        <Col xs={24} lg={6}>
                          <div className="revi-eval-wizard__reverseBox">
                            <Tooltip title={t("invertida.tooltipAsistente")}>
                              <Space size={8}>
                                <Tag color="orange">
                                  {t("invertida.etiqueta")}
                                </Tag>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "reverse"]}
                                  valuePropName="checked"
                                  style={{ margin: 0 }}
                                >
                                  <Switch />
                                </Form.Item>
                              </Space>
                            </Tooltip>

                            <div style={{ marginTop: 10 }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {t("asistente.paso3.vistaPrevia")}
                              </Text>
                              <div style={{ marginTop: 6 }}>
                                {previewFor(subtype, t)}
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  ))}

                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ id: "", text: "", reverse: false })}
                    block
                    style={{ marginTop: 12 }}
                  >
                    {t("asistente.paso3.anadirPregunta")}
                  </Button>

                  <Card
                    className="revi-eval-wizard__previewCard"
                    variant="outlined"
                    size="small"
                    style={{ marginTop: 12 }}
                  >
                    <Paragraph style={{ marginBottom: 0 }} type="secondary">
                      {t("asistente.paso3.consejo")}
                    </Paragraph>
                  </Card>
                </>
              )}
            </Form.List>
          </div>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            className="revi-eval-wizard__previewCard"
            variant="outlined"
            size="small"
            title={t("formato.titulo")}
          >
            <Space orientation="vertical" size={6} style={{ width: "100%" }}>
              <Text type="secondary">{subtype}</Text>
              {previewFor(subtype, t)}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
